const CONSENT_KEY = 'cookie_consent'
const CONSENT_DURATION_MS = 13 * 30 * 24 * 3600 * 1000

export interface AdsConsent {
  ad_storage: boolean
  ad_user_data: boolean
  ad_personalization: boolean
}

export interface CookieConsent {
  necessary: true
  analytics: boolean
  marketing: boolean
  adStorage: boolean
  adUserData: boolean
  adPersonalization: boolean
  timestamp: number
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof localStorage !== 'undefined'
}

function normalizeConsent(raw: any): CookieConsent | null {
  if (!raw || typeof raw !== 'object') return null
  const timestamp = Number(raw.timestamp || 0)
  if (!timestamp || Date.now() - timestamp > CONSENT_DURATION_MS) return null
  const marketing = raw.marketing === true
  return {
    necessary: true,
    analytics: raw.analytics === true,
    marketing,
    adStorage: raw.adStorage ?? raw.ad_storage ?? marketing,
    adUserData: raw.adUserData ?? raw.ad_user_data ?? marketing,
    adPersonalization: raw.adPersonalization ?? raw.ad_personalization ?? marketing,
    timestamp,
  }
}

export function getCookieConsent(): CookieConsent | null {
  if (!isBrowser()) return null
  try {
    const parsed = normalizeConsent(JSON.parse(localStorage.getItem(CONSENT_KEY) || 'null'))
    if (!parsed) localStorage.removeItem(CONSENT_KEY)
    return parsed
  } catch {
    return null
  }
}

export function getAdsConsent(): AdsConsent {
  const stored = getCookieConsent()
  return {
    ad_storage: stored?.adStorage === true,
    ad_user_data: stored?.adUserData === true,
    ad_personalization: stored?.adPersonalization === true,
  }
}

export function hasAdsConsent(): boolean {
  const consent = getAdsConsent()
  return consent.ad_storage && consent.ad_user_data && consent.ad_personalization
}

function ensureGtag() {
  if (!isBrowser()) return null
  const w = window as any
  w.dataLayer = w.dataLayer || []
  w.gtag = w.gtag || function gtag(...args: any[]) { w.dataLayer.push(args) }
  return w.gtag as (...args: any[]) => void
}

function toConsentMode(consent: AdsConsent) {
  return {
    ad_storage: consent.ad_storage ? 'granted' : 'denied',
    ad_user_data: consent.ad_user_data ? 'granted' : 'denied',
    ad_personalization: consent.ad_personalization ? 'granted' : 'denied',
    analytics_storage: getCookieConsent()?.analytics ? 'granted' : 'denied',
  }
}

/**
 * ID du tag Google Ads (ex. AW-…), injecté au build par Vite.
 * `import.meta.env` vaut `undefined` dans le bundle SSR (esbuild/Node) : sans ce garde-fou,
 * la lecture jetterait au CHARGEMENT du module et le serveur SSR ne démarrerait plus.
 */
function googleAdsTagId(): string {
  const env = import.meta.env as Record<string, string | undefined> | undefined
  if (!env) return ''
  return String(env.VITE_GOOGLE_ADS_ID || '').trim()
}

/**
 * Charge le tag Google Ads (gtag.js). À n'appeler QU'APRÈS `gtag('consent','default')` :
 * le tag lit le dataLayer existant, donc le Consent Mode s'applique dès le premier hit.
 *
 * Volontairement limité à `config` — AUCUN événement de conversion. La mesure des
 * conversions appartient à Adsis, qui les remonte côté serveur avec la MARGE comme valeur.
 * Ajouter ici un `gtag('event','conversion')` compterait chaque vente deux fois et
 * fausserait les enchères.
 */
function loadGoogleAdsTag() {
  const id = googleAdsTagId()
  if (!id || !isBrowser()) return
  const w = window as any
  if (w.__googleAdsTagLoaded) return // idempotent : hydratation / re-render
  w.__googleAdsTagLoaded = true

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`
  document.head.appendChild(script)

  const gtag = ensureGtag()
  if (!gtag) return
  gtag('js', new Date())
  gtag('config', id)
}

/**
 * Consent Mode v2, mode AVANCÉ : le tag se charge sur toutes les pages et envoie des
 * pings sans cookie tant que rien n'est accordé, au lieu de ne rien envoyer du tout.
 *
 * L'ORDRE est le contrat. `consent default` doit précéder tout hit, et les deux `set`
 * doivent précéder le chargement du tag — posés après, Google les ignore et le mode
 * retombe en « basique » sans le dire.
 */
export function initConsentMode() {
  const gtag = ensureGtag()
  if (!gtag) return
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    wait_for_update: 500,
  })

  // Expurge les identifiants publicitaires des requêtes tant que `ad_storage` est
  // refusé. Laissé actif en permanence : Google ne l'applique QUE dans ce cas, et
  // le basculer à l'acceptation ne ferait qu'ouvrir une fenêtre de fuite.
  gtag('set', 'ads_data_redaction', true)

  // Sans cookie, le `gclid` de la page d'atterrissage se perd à la première
  // navigation interne. `url_passthrough` le repropage dans les URL du site : c'est
  // ce qui rattache une conversion à son clic pour un visiteur qui a refusé.
  gtag('set', 'url_passthrough', true)

  const stored = getCookieConsent()
  if (stored) gtag('consent', 'update', toConsentMode(getAdsConsent()))
  // Après les défauts : le tag démarre donc en mode restreint tant que rien n'est accordé.
  chargerApresLePremierRendu(loadGoogleAdsTag)
}

/**
 * Diffère le chargement du tag hors du chemin critique.
 *
 * Il pèse 148 Ko et coûte ~68 ms d'exécution — mesuré par Lighthouse sur une
 * fiche produit, où il est la ressource la plus lourde après les contours SVG.
 * Il est déjà `async`, donc il ne BLOQUE pas le rendu, mais il concurrence
 * l'hydratation au moment précis où le navigateur essaie de peindre la page.
 * Sur un site dont la quasi-totalité du trafic est mobile, ces millisecondes
 * sont celles du LCP.
 *
 * Le contrat de Google reste respecté : `consent default` et les deux `set` sont
 * des écritures dans le `dataLayer`, déjà posées quand le script arrive — il les
 * rejoue à son chargement. Seule l'INJECTION est reportée.
 *
 * Trois déclencheurs, au premier qui vient :
 *   · une interaction du visiteur — celui qui s'engage est mesuré tout de suite,
 *     et c'est lui qui compte pour les enchères ;
 *   · un temps mort du navigateur, une fois la page peinte ;
 *   · un délai de sécurité, pour les navigateurs sans `requestIdleCallback` et
 *     pour les onglets qui n'atteignent jamais l'inactivité.
 *
 * La contrepartie est assumée : un visiteur qui repart en moins de deux secondes
 * sans rien toucher n'est plus compté. C'est un rebond — mais c'est bien une
 * perte de mesure, pas un gain gratuit.
 */
function chargerApresLePremierRendu(charger: () => void) {
  if (!isBrowser()) return
  const w = window as any
  let fait = false
  const declencher = () => {
    if (fait) return
    fait = true
    if (typeof w.removeEventListener === 'function') {
      for (const evt of INTERACTIONS) w.removeEventListener(evt, declencher)
    }
    charger()
  }
  // Un environnement sans écouteurs ni minuteur (test, rendu serveur exotique) ne
  // doit pas perdre le tag en silence : à défaut de pouvoir différer, on charge.
  const peutDifferer = typeof w.addEventListener === 'function'
    && (typeof w.requestIdleCallback === 'function' || typeof w.setTimeout === 'function')
  if (!peutDifferer) return charger()

  for (const evt of INTERACTIONS) w.addEventListener(evt, declencher, { once: true, passive: true })
  if (typeof w.requestIdleCallback === 'function') w.requestIdleCallback(declencher, { timeout: DELAI_TAG_MS })
  else w.setTimeout(declencher, DELAI_TAG_MS)
}

/** Une interaction charge le tag immédiatement : ce visiteur-là compte. */
const INTERACTIONS = ['pointerdown', 'keydown', 'touchstart', 'scroll'] as const

/** Filet de sécurité si le navigateur n'atteint jamais l'inactivité. */
const DELAI_TAG_MS = 2500

export function saveCookieConsent(consent: Omit<CookieConsent, 'necessary' | 'timestamp'>) {
  if (!isBrowser()) return
  const normalized: CookieConsent = {
    necessary: true,
    analytics: consent.analytics === true,
    marketing: consent.marketing === true,
    adStorage: consent.adStorage === true,
    adUserData: consent.adUserData === true,
    adPersonalization: consent.adPersonalization === true,
    timestamp: Date.now(),
  }
  localStorage.setItem(CONSENT_KEY, JSON.stringify(normalized))
  ensureGtag()?.('consent', 'update', toConsentMode(getAdsConsent()))
  window.dispatchEvent(new CustomEvent('cookie-consent-update', { detail: normalized }))
}
